import { FC, useEffect, useState } from 'react';
import {
  Button,
  Form,
  FormField,
  Heading,
  Input,
  Text,
} from '@livechat/design-system-react-components';

import { StandardError } from 'types/error';
import { EmailInUseError } from 'components/error-message/EmailInUseError';
import { UnexpectedError } from 'components/error-message/UnexpectedError';
import { Layout } from 'components/layout/Layout';
import { FormStates } from 'types/form';
import { getAccessToken } from 'helpers/account';
import Location from 'services/Location';
import { accountsClient } from 'services/connectivity/accounts/client';
import { validateEmail } from 'helpers/validators/email';

import * as styles from './styles';

export const SetEmail: FC = () => {
  const [error, setError] = useState<StandardError | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string>('');
  const [emailValidationError, setEmailValidationError] = useState<string | null>(null);
  const [formState, setFormState] = useState<string>(FormStates.Default);

  useEffect(() => {
    const token = getAccessToken();
    setAccessToken(token);

    if (!token) {
      setError({
        responseCode: 500,
      });
    }
  }, []);

  const handleEmailInputChange = (e) => {
    setEmail(e.target.value.trim());

    if (!validateEmail(e.target.value.trim())) {
      setEmailValidationError(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const emailValidation = validateEmail(email);

    if (emailValidation) {
      setEmailValidationError(emailValidation);

      return;
    }

    setFormState(FormStates.Requesting);

    const { error: updateError, response } = await accountsClient.updateMe({ email }, accessToken);

    if (updateError) {
      setError({
        requestId: updateError.request_id,
        description: updateError.error_description ?? '',
        responseCode: response.status ?? 500,
      });

      return;
    }

    window.location.href = `${window.location.origin}?restore_id=${encodeURIComponent(Location.getHashParam('state'))}`;
  };

  const handleResetError = (e) => {
    e.preventDefault();
    setError(null);
  };

  if (error) {
    switch (error.responseCode) {
      case 409:
        return <EmailInUseError onClick={handleResetError} />;
      default:
        return <UnexpectedError requestId={error.requestId} description={error.description} />;
    }
  }

  return (
    <Layout showHeaderLogo showGoToSignIn>
      <div css={styles.container}>
        <Heading size="lg">Set email</Heading>
        <Text>No worries! Just set your email.</Text>
        <Form onSubmit={handleSubmit}>
          <FormField
            labelText="Business email"
            labelFor="email"
            css={styles.formField}
            error={emailValidationError}
          >
            <Input
              type="email"
              name="email"
              placeholder="name@work-email.com"
              inputSize="large"
              value={email}
              onChange={handleEmailInputChange}
              autoFocus
            />
          </FormField>

          <Button
            loading={formState === FormStates.Requesting}
            css={styles.button}
            kind="primary"
            size="large"
            type="submit"
          >
            Continue
          </Button>
        </Form>
      </div>
    </Layout>
  );
};
